import { type FC, useMemo } from "react";

import { m } from "@recobra/i18n/messages";
import {
  type ChecklistRequirement,
  evaluateRequirements,
  resolutionChecklist,
  type Task,
} from "@recobra/schemas/task";
import type { TaskFlow } from "@recobra/schemas/task-catalog";

import type { TaskMetadataEditor } from "../task-details";
import { TaskModalSection } from "../task-display";
import { useContextLinkTargets } from "../task-links";
import { buildChecklistRows, type ChecklistRowEntry } from "./checklistRowGrouping";
import { checklistRowLink } from "./checklistRowLink";
import {
  ChecklistRowRenderer,
  type RequirementChecklistHandlers,
  type RequirementGroupSpec,
} from "./ChecklistRowRenderer";

const checklistRowKey = (row: ChecklistRowEntry<RequirementGroupSpec>): string =>
  row.type === "item" ? row.status.requirement.key : `group-${row.spec.key}`;

/** The flow's items for the chosen resolution, followed by the task's own requested documents. */
export const RequirementChecklist: FC<{
  task: Task;
  flow: Pick<TaskFlow, "checklist" | "groups"> | undefined;
  /** The chosen resolution; only items common to every resolution show before one is chosen. */
  resolutionId: string | null;
  canWaive?: boolean;
  disabled: boolean;
  handlers: RequirementChecklistHandlers;
  editor?: TaskMetadataEditor;
}> = ({ task, flow, resolutionId, canWaive = false, disabled, handlers, editor }) => {
  const targets = useContextLinkTargets(task.contextType);
  const requirementStatuses = useMemo(
    () =>
      evaluateRequirements({
        checklist: resolutionChecklist(flow?.checklist ?? [], resolutionId),
        requestedDocuments: task.requestedDocuments,
        progress: task.requirementProgress,
      }),
    [flow, resolutionId, task.requestedDocuments, task.requirementProgress],
  );

  if (requirementStatuses.length === 0) return null;

  const checklistRows = buildChecklistRows<RequirementGroupSpec>(requirementStatuses, [
    ...(flow?.groups ?? []).map((group): RequirementGroupSpec => ({
      key: group.id,
      includes: (requirement) =>
        requirement.kind === "checklist_item" && requirement.groupId === group.id,
      minItems: 1,
      title: group.label,
      defaultOpen: true,
      minimumSatisfied: group.minimumSatisfied,
    })),
    {
      key: "requested_documents",
      includes: ({ kind }) => kind === "requested_document",
      minItems: 1,
      title: m.task_requested_documents_label(),
      defaultOpen: true,
      editableField: "requestedDocuments",
    },
  ]);
  const linkFor = (requirement: ChecklistRequirement) =>
    checklistRowLink(requirement, task, targets);

  return (
    <TaskModalSection>
      <ul className="flex flex-col">
        {checklistRows.map((row) => (
          <ChecklistRowRenderer
            key={checklistRowKey(row)}
            row={row}
            task={task}
            canWaive={canWaive}
            disabled={disabled}
            handlers={handlers}
            linkFor={linkFor}
            editor={editor}
          />
        ))}
      </ul>
    </TaskModalSection>
  );
};
